﻿import { Card, CardBody } from "@progress/kendo-react-layout";
import { CreditCard, DollarSign, Megaphone, Target, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { SovraSkeleton } from "../../../components/skeleton/sovraSkeleton";
import type { CommercialPlanData, PlanByMediaTypeItem, PlanByPeriodItem } from "../../../models/commericalPlanModels";
import { formatCurrency, formatPercentage, isDefinedNumber } from "../../../utils/numberValueConversion";

type SummaryMetricFormat = "currency" | "percent" | "number";
type SummaryMetric = {
    title: string;
    value: number | null;
    format: SummaryMetricFormat;
    icon: React.ReactNode;
    delta?: string;
};

type SimulatorSummaryProps = {
    planData?: CommercialPlanData | null;
};

const sumNullable = (values: Array<number | null | undefined>): number | null => {
    const definedValues = values.filter(isDefinedNumber);

    if (definedValues.length === 0) {
        return null;
    }

    return definedValues.reduce<number>(
        (total: number, value: number): number => total + value,
        0
    );
};

const getPlanPeriods = (planData: CommercialPlanData): PlanByPeriodItem[] =>
    planData.plan_by_retailer.flatMap(
        (retailer): PlanByPeriodItem[] => retailer.plan_by_period
    );

const calculatePeriodRetailMediaSpend = (period: PlanByPeriodItem): number | null =>
    sumNullable(
        period.plan_by_media_type.map(
            (media: PlanByMediaTypeItem): number | null | undefined => media.media_type_spend
        )
    );

const calculateRetailMediaSpend = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map(
            (period: PlanByPeriodItem): number | null => calculatePeriodRetailMediaSpend(period)
        )
    );

const calculateTotalVelocityChange = (period: PlanByPeriodItem): number | null => {
    if (!isDefinedNumber(period.promotion_velocity_change)) {
        return null;
    }

    const mediaVelocityChanges = period.plan_by_media_type.map(
        (media: PlanByMediaTypeItem): number | null | undefined => media.media_type_velocity_change
    );

    if (!mediaVelocityChanges.every(isDefinedNumber)) {
        return null;
    }

    return period.promotion_velocity_change
        + mediaVelocityChanges.reduce<number>(
            (total: number, velocityChange: number): number => total + velocityChange,
            0
        );
};

const calculateExpectedVelocity = (period: PlanByPeriodItem): number | null => {
    const totalVelocityChange = calculateTotalVelocityChange(period);

    if (
        !isDefinedNumber(period.period_base_velocity)
        || !isDefinedNumber(period.number_stores ?? 1)
        || !isDefinedNumber(totalVelocityChange)
    ) {
        return null;
    }

    return period.period_base_velocity
        * (1 + totalVelocityChange)
        * (period.number_stores ?? 1);
};

const calculateProjectedRevenue = (planData: CommercialPlanData): number | null => sumNullable(
    getPlanPeriods(planData).map((period: PlanByPeriodItem): number | null => {
        const expectedVelocity = calculateExpectedVelocity(period);

        if (
            !isDefinedNumber(period.promotion_price)
            || !isDefinedNumber(expectedVelocity)
        ) {
            return null;
        }

        return period.promotion_price * expectedVelocity;
    })
);

const calculateProjectedProfit = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map((period: PlanByPeriodItem): number | null => {
            const expectedVelocity = calculateExpectedVelocity(period);
            const retailMediaSpend = calculatePeriodRetailMediaSpend(period);

            if (
                !isDefinedNumber(expectedVelocity)
                || !isDefinedNumber(period.promotion_price)
                || !isDefinedNumber(period.cost_per_unit)
                || !isDefinedNumber(retailMediaSpend)
            ) {
                return null;
            }

            return expectedVelocity
                * (period.promotion_price - period.cost_per_unit)
                - retailMediaSpend;
        })
    );

const calculateTradeSpend = (planData: CommercialPlanData): number | null =>
    sumNullable(
        getPlanPeriods(planData).map((period: PlanByPeriodItem): number | null => {
            const expectedVelocity = calculateExpectedVelocity(period);

            if (
                !isDefinedNumber(period.base_price)
                || !isDefinedNumber(period.promotion_price)
                || !isDefinedNumber(expectedVelocity)
            ) {
                return null;
            }

            return (period.base_price - period.promotion_price) * expectedVelocity;
        })
    );

const calculateRoi = (
    projectedRevenue: number | null,
    tradeSpend: number | null,
    retailMediaSpend: number | null
): number | null => {
    if (
        !isDefinedNumber(projectedRevenue)
        || !isDefinedNumber(tradeSpend)
        || !isDefinedNumber(retailMediaSpend)
    ) {
        return null;
    }

    const totalRetailInvestment = tradeSpend + retailMediaSpend;

    if (totalRetailInvestment === 0) {
        return null;
    }

    return projectedRevenue / totalRetailInvestment;
};

const renderSummarySkeletonCards = (): React.ReactNode => (
    <SummaryMetricsGrid>
        {Array.from({ length: 5 }, (_, index) => (
            <div key={index} className="col">
                <SummaryMetricCardShell>
                    <div className="d-flex align-items-start gap-2">
                        <SovraSkeleton
                            shape="rectangle"
                            style={{ width: 42, height: 42, borderRadius: 14 }}
                        />

                        {index < 2 && (
                            <SovraSkeleton
                                shape="rectangle"
                                style={{ width: 58, height: 24, borderRadius: 999 }}
                            />
                        )}
                    </div>

                    <div>
                        <div className="mb-2">
                            <SovraSkeleton
                                shape="text"
                                style={{ width: 124, height: 28 }}
                            />
                        </div>

                        <SovraSkeleton
                            shape="text"
                            style={{ width: 150, height: 18 }}
                        />
                    </div>
                </SummaryMetricCardShell>
            </div>
        ))}
    </SummaryMetricsGrid>
);

const getSummaryMetrics = (planData?: CommercialPlanData): SummaryMetric[] => {
    if (!planData) {
        return [];
    }

    const projectedRevenue = calculateProjectedRevenue(planData);
    const projectedProfit = calculateProjectedProfit(planData);
    const tradeSpend = calculateTradeSpend(planData);
    const retailMediaSpend = calculateRetailMediaSpend(planData);
    const roi = calculateRoi(projectedRevenue, tradeSpend, retailMediaSpend);

    return [
        {
            title: "Projected Revenue",
            value: projectedRevenue,
            format: "currency",
            icon: <DollarSign style={{ width: "14px", height: "14px" }} />,
            delta: "+12.4%"
        },
        {
            title: "Projected Profit",
            value: projectedProfit,
            format: "currency",
            icon: <TrendingUp style={{ width: "14px", height: "14px" }} />,
            delta: "+8.2%"
        },
        {
            title: "ROI",
            value: roi,
            format: "percent",
            icon: <Target style={{ width: "14px", height: "14px" }} />
        },
        {
            title: "Trade Spend",
            value: tradeSpend,
            format: "currency",
            icon: <CreditCard style={{ width: "14px", height: "14px" }} />
        },
        {
            title: "Retail Media Spend",
            value: retailMediaSpend,
            format: "currency",
            icon: <Megaphone style={{ width: "14px", height: "14px" }} />,
            delta: "+12.4%"
        }
    ];
};

const formatMetricValue = (metric: SummaryMetric): string => {
    switch (metric.format) {
        case "currency":
            return formatCurrency(metric.value);
        case "percent":
            return formatPercentage(metric.value);
        case "number":
            return metric.value?.toLocaleString() ?? "-";
    }
};

const SummaryMetricCardShell = ({ children }: { children: React.ReactNode }) => {
    return (
        <Card className="simulator-summary-card border rounded-3 h-100 w-100 shadow-sm shadow-md-hover">
            <CardBody className="d-flex flex-column gap-2">{children}</CardBody>
        </Card>
    );
};

const SummaryMetricsGrid = ({ children }: { children: React.ReactNode }) => {
    return (
        <div className="container-fluid px-0">
            <div className="row g-2 row-cols-1 row-cols-sm-2 row-cols-xl-5">
                {children}
            </div>
        </div>
    );
};

const SimulatorSummary: React.FC<SimulatorSummaryProps> = ({ planData }) => {
    const metrics = useMemo<SummaryMetric[]>(
        () => planData ? getSummaryMetrics(planData) : [],
        [planData]
    );

    if (!planData) {
        return (
            <div className="container-fluid px-1">
                <div className="row g-2">
                    {renderSummarySkeletonCards()}
                </div>
            </div>
        );
    }

    return (
        <SummaryMetricsGrid>
            {metrics.map((metric, index) => (
                <div
                    key={`summary_${index}`}
                    className="col d-flex"
                >
                    <SummaryMetricCardShell>
                        <div className="simulator-summary-metrics d-flex gap-2 min-w-0">
                            <span className="simulator-summary-icon d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0">
                                {metric.icon}
                            </span>

                            <div className="simulator-summary-value fw-bold text-body lh-sm mb-0 min-w-0 flex-md-grow-1">
                                {formatMetricValue(metric)}
                            </div>

                            {isDefinedNumber(metric.value) && metric.delta && (
                                <span className="badge rounded-pill fw-semibold simulator-summary-delta flex-shrink-0">
                                    {metric.delta}
                                </span>
                            )}
                        </div>

                        <p className="simulator-summary-title text-secondary mb-0">
                            {metric.title}
                        </p>
                    </SummaryMetricCardShell>
                </div>
            ))}
        </SummaryMetricsGrid>
    );
};

export default SimulatorSummary;
